// NetPosition.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import './NetPosition.css';
import RefreshButton from './RefreshButton';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Valid index names per backend/NFO instruments
const INDEX_OPTIONS = [
  { label: 'NIFTY', value: 'NIFTY' },
  { label: 'BANKNIFTY', value: 'BANKNIFTY' },
  { label: 'FINNIFTY', value: 'FINNIFTY' },
  { label: 'MIDCPNIFTY', value: 'MIDCPNIFTY' },
];

const NetPosition = () => {
  const [indexVal, setIndexVal] = useState('NIFTY');
  const [chartData, setChartData] = useState(null);
  const [pcrValue, setPcrValue] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchPCR = useCallback(async (idx) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/pcr?index=${encodeURIComponent(idx)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json(); // { success, message, data }

      if (!json.success) {
        throw new Error(json.message || 'Request failed');
      }

      const toNumber = (value) => {
        if (value == null) return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        if (typeof value === 'string') {
          const num = parseFloat(value.replace(/,/g, ''));
          return Number.isFinite(num) ? num : 0;
        }
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };

      const payload = json.data || {};
      const callOiChange = toNumber(json.total_call_oi_change_lots ?? payload.total_call_oi_change_lots);
      const putOiChange = toNumber(json.total_put_oi_change_lots ?? payload.total_put_oi_change_lots);
      const callOi = toNumber(json.total_call_oi_lots ?? payload.total_call_oi_lots ?? payload.total_call_oi);
      const putOi = toNumber(json.total_put_oi_lots ?? payload.total_put_oi_lots ?? payload.total_put_oi);

      const callValue = callOiChange !== 0 || putOiChange !== 0 ? callOiChange : callOi;
      const putValue = callOiChange !== 0 || putOiChange !== 0 ? putOiChange : putOi;

      setChartData({
        labels: ['Open Interest'],
        datasets: [
          {
            label: callOiChange !== 0 || putOiChange !== 0 ? 'Call OI Change (Lots)' : 'Total Call OI (Lots)',
            data: [callValue],
            backgroundColor: '#ef4444',
          },
          {
            label: callOiChange !== 0 || putOiChange !== 0 ? 'Put OI Change (Lots)' : 'Total Put OI (Lots)',
            data: [putValue],
            backgroundColor: '#22c55e',
          },
        ],
      });

      const pcr = json.pcr_oi_change ?? payload.pcr_oi_change ?? payload.pcr_lots ?? payload.pcr;
      setPcrValue(pcr);
    } catch (err) {
      setError(err.message || 'Failed to fetch PCR');
      setChartData(null);
      setPcrValue(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPCR(indexVal);
  }, [indexVal, fetchPCR]);

  const options = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { mode: 'nearest', intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: 'rgba(0,0,0,0.08)' } },
    },
  };

  return (
    <div className="net-position-container">
      <div className="search-bar">
        <select
          className="index-select"
          value={indexVal}
          onChange={(e) => setIndexVal(e.target.value)}
          disabled={loading}
          title="Select Index"
        >
          {INDEX_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button className="go-btn" onClick={() => fetchPCR(indexVal)} disabled={loading}>
          {loading ? 'Loading...' : 'GO'}
        </button>
        <RefreshButton 
          onRefresh={() => fetchPCR(indexVal)} 
          isLoading={loading}
          size="small"
          style={{ marginLeft: '10px' }}
        />
      </div>

      <h2 className="chart-title">Net Position</h2>

      {error ? (
        <p className="error-text">{error}</p>
      ) : chartData ? (
        <Bar data={chartData} options={options} />
      ) : (
        <p>Loading...</p>
      )}

      <div className="pcr-text">PCR: {pcrValue ?? (loading ? 'Loading...' : '—')}</div>
    </div>
  );
};

export default NetPosition;
